// frontend/src/components/Hero.jsx
import { useMemo } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { uploadsUrl } from "../utils/api";
import "./Hero.css";

function extractYoutubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:shorts\/|watch\?v=|embed\/))([^?&/]+)/);
  return m ? m[1] : null;
}

// Some projects already store the word "Starting" inside the price field
// itself (e.g. "Starting ₹2.00 Cr"), others just store the amount
// ("₹68 Lakh"). Avoid ever showing it twice on the mobile price pill.
function withStartingPrefix(price) {
  if (!price) return "";
  return /^starting\b/i.test(price.trim()) ? price.trim() : `Starting ${price}`;
}

export default function Hero({ projectId, header, onCtaClick }) {
  const { projectName, developerName, projectSubheading, startingPrice, siteVisitText,
          youtubeVideoUrl, mobileReelUrl, floorOfferText, projectNameAgainstSiteName } = header;

  const mobileId  = useMemo(() => extractYoutubeId(mobileReelUrl),  [mobileReelUrl]);
  const desktopId = useMemo(() => extractYoutubeId(youtubeVideoUrl), [youtubeVideoUrl]);
  const hasMedia  = mobileId || desktopId;

  // The promo creative shows two offer rows ("Flexi Payment plan",
  // "Spot Booking Offers"). The CMS only exposes one free-text field for
  // this, so a project can list several offers by separating them with "|"
  // in floorOfferText; otherwise it renders as a single row, and if the
  // field is empty at all we fall back to evergreen booking-card copy
  // rather than showing an empty strip.
  const offerItems = useMemo(() => {
    if (floorOfferText) {
      return floorOfferText.split("|").map((s) => s.trim()).filter(Boolean);
    }
    return ["Flexi Payment Plans", "Limited Period Offers"];
  }, [floorOfferText]);

  const bgStyle = !hasMedia ? {
    "--bg1": `url('${uploadsUrl(projectId, "background1.webp")}')`,
    "--bg2": `url('${uploadsUrl(projectId, "background2.webp")}')`,
    "--bg3": `url('${uploadsUrl(projectId, "background3.webp")}')`,
  } : {};

  return (
    <div className="hero-wrap" id="home">
      <header
        className={`hero-header${!hasMedia ? " no-media" : ""}`}
        style={bgStyle}
        aria-label="Hero section"
      >
        {/* Background media */}
        {hasMedia && (
          <div className="hero-media">
            {mobileId && (
              <iframe
                className="hero-media__iframe hero-media__iframe--mobile"
                src={`https://www.youtube.com/embed/${mobileId}?autoplay=1&mute=1&loop=1&controls=0&playlist=${mobileId}&modestbranding=1&showinfo=0&rel=0`}
                allow="autoplay; fullscreen"
                title="Background reel"
              />
            )}
            {desktopId && (
              <iframe
                className="hero-media__iframe hero-media__iframe--desktop"
                src={`https://www.youtube.com/embed/${desktopId}?autoplay=1&mute=1&loop=1&controls=0&playlist=${desktopId}&modestbranding=1&showinfo=0&rel=0`}
                allow="autoplay; fullscreen"
                title="Background video"
              />
            )}
          </div>
        )}

        {/* Gradient overlay */}
        <div className="hero-overlay" aria-hidden="true" />

        {/* Mobile-only simplified overlay — name + starting price share a
            row directly on the image, with location on the line below.
            Hidden on desktop, where the full .hero-card below is used
            instead (see Hero.css). */}
        <div className="hero-mobile-overlay">
          <div className="hero-mobile-overlay__row">
            <h2 className="hero-mobile-overlay__name">{projectName}</h2>
            {startingPrice && (
              <span className="hero-mobile-price-badge">
                {withStartingPrefix(startingPrice)}
              </span>
            )}
          </div>
          {projectNameAgainstSiteName && (
            <p className="hero-mobile-overlay__location">
              <FaMapMarkerAlt aria-hidden="true" />
              {projectNameAgainstSiteName}
            </p>
          )}
        </div>
      </header>

      {/* Booking card — floats over the image on desktop. Hidden on mobile,
          where the simplified overlay above + CTA strip below take over
          (see Hero.css). */}
      <div className="hero-card">
        <div className="hero-card__badge">Booking Open</div>

        <div className="hero-card__id">
          <h2 className="hero-card__name">{projectName}</h2>
          {developerName && <p className="hero-card__developer">By {developerName}</p>}
          {projectNameAgainstSiteName && (
            <p className="hero-card__location">{projectNameAgainstSiteName}</p>
          )}
        </div>

        {offerItems.length > 0 && (
          <ul className="hero-card__offers">
            {offerItems.slice(0, 3).map((item, i) => (
              <li key={i} className="hero-card__offer-row">
                <span className="hero-card__diamond" aria-hidden="true">◆</span>
                {item}
              </li>
            ))}
          </ul>
        )}

        <div className="hero-card__price-block">
          {projectSubheading && <p className="hero-card__tagline">{projectSubheading}</p>}
          {startingPrice && (
            <p className="hero-card__starting-label">Starting At</p>
          )}
          {startingPrice && (
            <button
              className="hero-card__price"
              onClick={() => document.querySelector(".apartment-section")?.scrollIntoView({ behavior: "smooth" })}
            >
              {startingPrice} <span className="hero-card__price-suffix">Onwards</span>
            </button>
          )}
          <button
            className="hero-card__cta"
            onClick={() => onCtaClick("Site Visit")}
          >
            {siteVisitText || "Enquire Now"}
          </button>
        </div>
      </div>

      {/* Mobile-only CTA strip — replaces the desktop hero-card's CTA on
          small screens with a single full-width booking button. */}
      <div className="hero-mobile-strip">
        <button
          className="hero-mobile-strip__cta"
          onClick={() => onCtaClick("Site Visit")}
        >
          {siteVisitText || "Book A Free Site Visit"}
        </button>
      </div>
    </div>
  );
}
